import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ArrowRightOnRectangleIcon,
  ArrowTopRightOnSquareIcon,
  Bars3Icon,
  ChartBarIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  Squares2X2Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import DashboardHeader from './DashboardHeader';
import SidebarLink from './SidebarLink';
import SiteFooter from './SiteFooter';
import { WEALTH_PORTAL_URL } from '../constants/externalLinks';
import { useAppLogout } from '../hooks/useAppLogout';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

function SidebarContents() {
  return (
    <>
      {/* OVERVIEW */}
      <div className="mb-2.5 px-6">
        <p className="dash-sidebar__label mb-2 text-[10.5px] font-semibold uppercase">
          Overview
        </p>
      </div>
      <nav className="mb-7">
        <SidebarLink to="/">
          <Squares2X2Icon className="h-4 w-4" />
          Dashboard
        </SidebarLink>
      </nav>

      {/* YOUR SERVICES */}
      <div className="mb-2.5 px-6">
        <p className="dash-sidebar__label mb-2 text-[10.5px] font-semibold uppercase">
          Your Services
        </p>
      </div>
      <nav className="mb-7 space-y-0.5">
        <SidebarLink to="/trading">
          <ChartBarIcon className="h-4 w-4" />
          Trading
        </SidebarLink>
        <a
          href={WEALTH_PORTAL_URL || '#'}
          target="_blank"
          rel="noreferrer"
          className="dash-nav-link flex items-center gap-3 px-6 py-2.5 text-sm font-medium"
        >
          <CurrencyDollarIcon className="h-4 w-4" />
          <span className="flex-1">Wealth</span>
          <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
        </a>
      </nav>

      {/* ACCOUNT */}
      <div className="mb-2.5 px-6">
        <p className="dash-sidebar__label mb-2 text-[10.5px] font-semibold uppercase">
          Account
        </p>
      </div>
      <nav className="mb-7 space-y-0.5">
        <SidebarLink to="/orders">
          <DocumentTextIcon className="h-4 w-4" />
          My orders
        </SidebarLink>
        <span className="dash-nav-disabled flex items-center gap-3 px-6 py-2.5 text-sm font-medium">
          <DocumentTextIcon className="h-4 w-4" />
          <span className="flex-1">My documents</span>
          <span className="dash-soon-badge rounded px-1.5 py-0.5 text-[9.5px] font-semibold uppercase">
            Soon
          </span>
        </span>
      </nav>

      {/* SUPPORT */}
      <div className="dash-sidebar__support-border mb-2.5 px-6 pt-5">
        <p className="dash-sidebar__label mb-2 text-[10.5px] font-semibold uppercase">
          Support
        </p>
      </div>
      <nav className="space-y-0.5">
        <SidebarLink href="mailto:support@dag.com">
          <EnvelopeIcon className="h-4 w-4" />
          Email support
        </SidebarLink>
      </nav>
    </>
  );
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const handleLogout = useAppLogout();
  const { user } = useAuth0();

  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (drawerOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflowX = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflowX = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflowX = '';
    };
  }, [drawerOpen]);

  return (
    <div className="dash-shell flex h-screen flex-col">
      <DashboardHeader />

      {!drawerOpen && (
        <button
          className="dash-hamburger md:hidden"
          onClick={() => setDrawerOpen(true)}
          aria-label="Open navigation"
        >
          <Bars3Icon className="h-5 w-5" />
        </button>
      )}

      {/* Backdrop */}
      {drawerOpen && (
        <div
          className="dash-drawer-backdrop md:hidden"
          onClick={() => setDrawerOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Mobile slide-in drawer */}
      <aside
        className={`dash-drawer md:hidden${drawerOpen ? ' dash-drawer--open' : ''}`}
        aria-hidden={!drawerOpen}
      >
        <div className="flex items-center justify-between px-6 py-[22px]">
          <p className="dash-sidebar__label text-[10.5px] font-semibold uppercase">
            Navigation
          </p>
          <button
            onClick={() => setDrawerOpen(false)}
            aria-label="Close navigation"
            className="dash-drawer-close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>
        <SidebarContents />
        <div className="dash-sidebar__support-border mt-auto px-6 pt-5">
          {(user?.name || user?.email) && (
            <div className="mb-4">
              <p className="dash-sidebar__label mb-0.5 text-[10.5px] font-semibold uppercase">
                Signed in as
              </p>
              <p className="text-sm font-medium text-on-surface truncate">
                {user?.name || user?.email}
              </p>
            </div>
          )}
          <button
            onClick={() => {
              setDrawerOpen(false);
              handleLogout();
            }}
            className="dash-nav-link dash-nav-link--danger flex w-full items-center gap-3 px-6 py-2.5 text-sm font-medium"
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4" />
            Logout
          </button>
        </div>
      </aside>

      {/* Body: desktop sidebar + main */}
      <div className="flex flex-1 overflow-hidden">
        {/* Desktop sidebar — hidden on mobile */}
        <aside
          className="dash-sidebar hidden w-[260px] flex-shrink-0 flex-col py-7 md:flex"
          style={{ overflow: 'visible' }}
        >
          <SidebarContents />
        </aside>

        {/* Main content */}
        <main className="flex-1 overflow-y-auto">{children}</main>
      </div>

      {/* Footer — pinned to the bottom of the viewport like the header */}
      <SiteFooter />
    </div>
  );
}
